// Logique du suivi de match 5-1 : rotations, alternance service/réception, score, fin de set.
// Tout est pur (aucun React) pour pouvoir être testé : voir matchTracker.test.ts.

export type Phase = "service" | "reception";
export type Rotation = 1 | 2 | 3 | 4 | 5 | 6;
export type Team = "us" | "them";
export type Score = { us: number; them: number };

export const ROTATIONS: Rotation[] = [1, 2, 3, 4, 5, 6];
// Ordre de passage quand on récupère le service : P1 → P6 → P5 → P4 → P3 → P2 → P1.
export const ROTATION_CYCLE: Rotation[] = [1, 6, 5, 4, 3, 2];

export const nextRotation = (r: Rotation): Rotation => (r === 1 ? 6 : ((r - 1) as Rotation));
export const otherPhase = (p: Phase): Phase => (p === "service" ? "reception" : "service");
export const phaseLabel = (p: Phase): string => (p === "service" ? "Service" : "Réception");

// ───────────────────────────── Réglages & état ─────────────────────────────

export type MatchSettings = {
  scoring: boolean;
  pointsPerSet: number;
  tieBreakPoints: number;
  // Nombre de sets à gagner : 2 = au meilleur de 3, 3 = au meilleur de 5.
  setsToWin: 2 | 3;
  winByTwo: boolean;
};

export const DEFAULT_SETTINGS: MatchSettings = {
  scoring: false,
  pointsPerSet: 25,
  tieBreakPoints: 15,
  setsToWin: 2,
  winByTwo: true,
};

export type SetRecord = Score & { winner: Team | null };

// Noms affichés sur l'écran de fin et l'image partagée.
export const TEAM_NAMES: Record<Team, string> = { us: "Ensisheim", them: "Adversaire" };

export type MatchState = {
  // setup = écran de départ du set ; playing = suivi en cours ; finished = match terminé (mode score).
  status: "setup" | "playing" | "finished";
  setNumber: number;
  rotation: Rotation;
  phase: Phase;
  score: Score;
  sets: SetRecord[];
  // Choix faits au départ du set courant (sert à proposer les valeurs du set suivant).
  startRotation: Rotation;
  startPhase: Phase;
  // Set gagné en attente de validation (mode score) : permet d'annuler un point saisi par erreur.
  setEnd: Team | null;
  sideSwitchDone: boolean;
};

export type Tracker = {
  settings: MatchSettings;
  present: MatchState;
  past: MatchState[];
};

export const INITIAL_STATE: MatchState = {
  status: "setup",
  setNumber: 1,
  rotation: 1,
  phase: "reception",
  score: { us: 0, them: 0 },
  sets: [],
  startRotation: 1,
  startPhase: "reception",
  setEnd: null,
  sideSwitchDone: false,
};

export const INITIAL_TRACKER: Tracker = { settings: DEFAULT_SETTINGS, present: INITIAL_STATE, past: [] };

const MAX_HISTORY = 500;

// ───────────────────────────── Règles du score ─────────────────────────────

export function setsWon(sets: SetRecord[]): Score {
  return {
    us: sets.filter((s) => s.winner === "us").length,
    them: sets.filter((s) => s.winner === "them").length,
  };
}

// Set décisif (tie-break) : les deux équipes à un set de la victoire.
export function isDecidingSet(settings: MatchSettings, sets: SetRecord[]): boolean {
  const won = setsWon(sets);
  return settings.scoring && won.us === settings.setsToWin - 1 && won.them === settings.setsToWin - 1;
}

export function targetPoints(settings: MatchSettings, sets: SetRecord[]): number {
  return isDecidingSet(settings, sets) ? settings.tieBreakPoints : settings.pointsPerSet;
}

export function setWinner(score: Score, target: number, winByTwo: boolean): Team | null {
  if (Math.max(score.us, score.them) < target) return null;
  if (winByTwo && Math.abs(score.us - score.them) < 2) return null;
  if (score.us === score.them) return null;
  return score.us > score.them ? "us" : "them";
}

export function matchWinner(settings: MatchSettings, sets: SetRecord[]): Team | null {
  const won = setsWon(sets);
  if (won.us >= settings.setsToWin) return "us";
  if (won.them >= settings.setsToWin) return "them";
  return null;
}

// Résultat d'un match clos (automatiquement ou à la main) : qui a le plus de sets ; null = égalité.
export function matchResult(sets: SetRecord[]): Team | null {
  const won = setsWon(sets);
  if (won.us === won.them) return null;
  return won.us > won.them ? "us" : "them";
}

// Changement de côté au tie-break : dès qu'une équipe atteint la moitié des points (8 sur 15).
export function sideSwitchDue(settings: MatchSettings, s: MatchState): boolean {
  if (s.status !== "playing" || s.sideSwitchDone || !isDecidingSet(settings, s.sets)) return false;
  return Math.max(s.score.us, s.score.them) >= Math.ceil(settings.tieBreakPoints / 2);
}

// Valeurs proposées à l'écran de départ : même rotation que le set précédent, service/réception inversés.
export function defaultSetup(s: MatchState): { rotation: Rotation; phase: Phase } {
  return {
    rotation: s.startRotation,
    phase: s.setNumber === 1 ? s.startPhase : otherPhase(s.startPhase),
  };
}

// ───────────────────────────── Transitions ─────────────────────────────

// Changement de service. En réception on récupère le service : l'équipe tourne (rotation suivante).
// Au service on le perd : retour en réception, sans rotation.
export function applyStep(s: MatchState): MatchState {
  return s.phase === "reception"
    ? { ...s, phase: "service", rotation: nextRotation(s.rotation) }
    : { ...s, phase: "reception" };
}

function withSetEnd(settings: MatchSettings, s: MatchState): MatchState {
  if (!settings.scoring || s.status !== "playing") return { ...s, setEnd: null };
  return { ...s, setEnd: setWinner(s.score, targetPoints(settings, s.sets), settings.winByTwo) };
}

export function applyPoint(settings: MatchSettings, s: MatchState, team: Team): MatchState {
  if (!settings.scoring || s.status !== "playing" || s.setEnd) return s;
  let next: MatchState = { ...s, score: { ...s.score, [team]: s.score[team] + 1 } };
  if (team === "us" && s.phase === "reception") next = applyStep(next);
  if (team === "them" && s.phase === "service") next = applyStep(next);
  return withSetEnd(settings, next);
}

// Fin de set. Le score vient du pointage (mode score) ou est saisi à la main à la fin du set
// (mode sans score) : dans les deux cas le set est retenu et le match se termine au bon nombre de sets.
function finishSet(settings: MatchSettings, s: MatchState, entered?: Score): MatchState {
  const final = entered ?? (settings.scoring ? s.score : null);
  const played = final !== null && final.us + final.them > 0;
  const winner: Team | null = final ? s.setEnd ?? (final.us === final.them ? null : final.us > final.them ? "us" : "them") : null;
  const sets = final && played ? [...s.sets, { ...final, winner }] : s.sets;
  const over = matchWinner(settings, sets) !== null;
  return {
    ...s,
    sets,
    status: over ? "finished" : "setup",
    setNumber: over ? s.setNumber : s.setNumber + 1,
    score: { us: 0, them: 0 },
    setEnd: null,
    sideSwitchDone: false,
  };
}

export type Action =
  | { type: "startSet"; rotation: Rotation; phase: Phase }
  | { type: "step" }
  | { type: "point"; team: Team }
  | { type: "correct"; rotation: Rotation; phase: Phase; score?: Score }
  | { type: "nextSet"; score?: Score }
  // Clôture manuelle depuis l'écran de départ d'un set (le match s'est arrêté avant le nombre de sets gagnants).
  | { type: "finishMatch" }
  | { type: "ackSideSwitch" }
  | { type: "settings"; settings: MatchSettings }
  | { type: "undo" }
  | { type: "reset" };

const clampInt = (v: unknown, min: number, max: number, fallback: number): number => {
  const n = Math.round(Number(v));
  return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : fallback;
};

export function sanitizeSettings(raw: Partial<MatchSettings> | null | undefined): MatchSettings {
  const d = DEFAULT_SETTINGS;
  return {
    scoring: raw?.scoring === true,
    pointsPerSet: clampInt(raw?.pointsPerSet, 1, 99, d.pointsPerSet),
    tieBreakPoints: clampInt(raw?.tieBreakPoints, 1, 99, d.tieBreakPoints),
    setsToWin: raw?.setsToWin === 3 ? 3 : 2,
    winByTwo: raw?.winByTwo !== false,
  };
}

// Historique : chaque action qui modifie le match empile l'état précédent (annulation illimitée).
function commit(t: Tracker, next: MatchState): Tracker {
  if (next === t.present) return t;
  return { ...t, present: next, past: [...t.past, t.present].slice(-MAX_HISTORY) };
}

export function trackerReducer(t: Tracker, action: Action): Tracker {
  const s = t.present;
  switch (action.type) {
    case "startSet":
      if (s.status !== "setup") return t;
      return commit(t, {
        ...s,
        status: "playing",
        rotation: action.rotation,
        phase: action.phase,
        startRotation: action.rotation,
        startPhase: action.phase,
        score: { us: 0, them: 0 },
        setEnd: null,
        sideSwitchDone: false,
      });
    case "step":
      return s.status === "playing" && !s.setEnd ? commit(t, applyStep(s)) : t;
    case "point":
      return commit(t, applyPoint(t.settings, s, action.team));
    case "correct": {
      if (s.status !== "playing") return t;
      const score = action.score
        ? { us: clampInt(action.score.us, 0, 99, s.score.us), them: clampInt(action.score.them, 0, 99, s.score.them) }
        : s.score;
      return commit(t, withSetEnd(t.settings, { ...s, rotation: action.rotation, phase: action.phase, score }));
    }
    case "nextSet": {
      if (s.status !== "playing") return t;
      const entered = action.score ? { us: clampInt(action.score.us, 0, 99, 0), them: clampInt(action.score.them, 0, 99, 0) } : undefined;
      return commit(t, finishSet(t.settings, s, entered));
    }
    case "finishMatch":
      return s.status === "setup" && s.sets.length > 0 ? commit(t, { ...s, status: "finished", setNumber: s.sets.length }) : t;
    case "ackSideSwitch":
      return { ...t, present: { ...s, sideSwitchDone: true } };
    case "settings": {
      const settings = sanitizeSettings(action.settings);
      return { ...t, settings, present: withSetEnd(settings, s) };
    }
    case "undo": {
      if (t.past.length === 0) return t;
      return { ...t, present: t.past[t.past.length - 1], past: t.past.slice(0, -1) };
    }
    case "reset":
      return { settings: t.settings, present: INITIAL_STATE, past: [] };
    default:
      return t;
  }
}

// ───────────────────────────── Sauvegarde locale ─────────────────────────────
// Un match dure longtemps et le téléphone se verrouille : on reprend exactement où on en était.

const TRACKER_KEY = "volley-match-tracker-v1";
const NAMES_KEY = "volley-match-names-v1";

const isRotation = (v: unknown): v is Rotation => typeof v === "number" && v >= 1 && v <= 6 && Number.isInteger(v);
const isPhase = (v: unknown): v is Phase => v === "service" || v === "reception";
const isTeam = (v: unknown): v is Team | null => v === "us" || v === "them" || v === null;

function sanitizeState(raw: any): MatchState | null {
  if (!raw || typeof raw !== "object") return null;
  if (!["setup", "playing", "finished"].includes(raw.status)) return null;
  if (!isRotation(raw.rotation) || !isPhase(raw.phase) || !isRotation(raw.startRotation) || !isPhase(raw.startPhase)) return null;
  const sets: SetRecord[] = Array.isArray(raw.sets)
    ? raw.sets
        .filter((x: any) => x && Number.isFinite(x.us) && Number.isFinite(x.them) && isTeam(x.winner ?? null))
        .map((x: any) => ({ us: x.us, them: x.them, winner: x.winner ?? null }))
    : [];
  return {
    status: raw.status,
    setNumber: clampInt(raw.setNumber, 1, 99, 1),
    rotation: raw.rotation,
    phase: raw.phase,
    score: { us: clampInt(raw.score?.us, 0, 99, 0), them: clampInt(raw.score?.them, 0, 99, 0) },
    sets,
    startRotation: raw.startRotation,
    startPhase: raw.startPhase,
    setEnd: raw.setEnd === "us" || raw.setEnd === "them" ? raw.setEnd : null,
    sideSwitchDone: raw.sideSwitchDone === true,
  };
}

export function loadTracker(): Tracker {
  try {
    const raw = JSON.parse(window.localStorage.getItem(TRACKER_KEY) || "null");
    const present = sanitizeState(raw?.present);
    if (!present) return INITIAL_TRACKER;
    const past: MatchState[] = (Array.isArray(raw.past) ? raw.past : [])
      .map(sanitizeState)
      .filter((x: MatchState | null): x is MatchState => x !== null)
      .slice(-MAX_HISTORY);
    return { settings: sanitizeSettings(raw.settings), present, past };
  } catch {
    return INITIAL_TRACKER;
  }
}

export function saveTracker(t: Tracker): void {
  try {
    window.localStorage.setItem(TRACKER_KEY, JSON.stringify(t));
  } catch {
    // stockage indisponible (navigation privée, quota…) : le suivi fonctionne quand même.
  }
}

// ───────────────────────────── Noms des joueuses ─────────────────────────────
// Les pastilles des schémas sont des rôles. "Sub_R4" est la 2e R4 (deux R4 sur le terrain) : celle
// qui est devant au filet quand la passeuse est en P1 ; "R4" est alors derrière. De même "C1" est la
// centrale devant en P1 et "C2" celle derrière. "Sub_Lib" / "Sub_C2" représentent la même joueuse
// que "Lib" / "C2".

export type NameSlot = "Pass" | "Poi" | "Lib" | "R4" | "R4b" | "C1" | "C2";
export type PlayerNames = Partial<Record<NameSlot, string>>;

export const NAME_SLOTS: { slot: NameSlot; label: string; color: string }[] = [
  { slot: "Pass", label: "Passeuse", color: "#ef4444" },
  { slot: "Poi", label: "Pointue", color: "#8b5cf6" },
  { slot: "R4b", label: "R4 avant (devant en P1)", color: "#f59e0b" },
  { slot: "R4", label: "R4 arrière (derrière en P1)", color: "#f59e0b" },
  { slot: "C1", label: "Centrale avant (devant en P1)", color: "#3b82f6" },
  { slot: "C2", label: "Centrale arrière (derrière en P1)", color: "#ec4899" },
  { slot: "Lib", label: "Libéro", color: "#1a1a1a" },
];

export function slotOfPuck(puckId: string): NameSlot {
  if (puckId === "Sub_R4") return "R4b";
  return (puckId.startsWith("Sub_") ? puckId.slice(4) : puckId) as NameSlot;
}

// Libellé court d'un rôle, pour une phrase (« Léa (libéro) »).
export const SLOT_SHORT: Record<NameSlot, string> = {
  Pass: "passeuse",
  Poi: "pointue",
  R4b: "R4 avant",
  R4: "R4 arrière",
  C1: "centrale avant",
  C2: "centrale arrière",
  Lib: "libéro",
};

export type Substitution = { in: NameSlot; out: NameSlot };

// Remplacement entre deux positions affichées : une joueuse sort, une autre entre (typiquement la
// libéro et une centrale). Comparé par rôle, donc insensible aux pastilles Sub_* (même joueuse).
export function substitution(prev: CourtView | null | undefined, next: CourtView | null | undefined): Substitution | null {
  if (!prev || !next) return null;
  const slots = (v: CourtView): NameSlot[] => v.pucks.map((p) => slotOfPuck(p.id)).filter((x, i, arr) => arr.indexOf(x) === i);
  const before = slots(prev);
  const after = slots(next);
  const entering = after.filter((x) => !before.includes(x));
  const leaving = before.filter((x) => !after.includes(x));
  return entering.length === 1 && leaving.length === 1 ? { in: entering[0], out: leaving[0] } : null;
}

export function substitutionLabel(sub: Substitution, names: PlayerNames): string {
  const who = (slot: NameSlot) => (names[slot] ? `${names[slot]} (${SLOT_SHORT[slot]})` : SLOT_SHORT[slot]);
  const s = who(sub.in);
  return `${s.charAt(0).toUpperCase()}${s.slice(1)} remplace ${who(sub.out)}`;
}

export function loadNames(): PlayerNames {
  try {
    const raw = JSON.parse(window.localStorage.getItem(NAMES_KEY) || "null");
    const names: PlayerNames = {};
    NAME_SLOTS.forEach(({ slot }) => {
      if (typeof raw?.[slot] === "string" && raw[slot].trim()) names[slot] = raw[slot].trim().slice(0, 14);
    });
    return names;
  } catch {
    return {};
  }
}

export function saveNames(names: PlayerNames): void {
  try {
    window.localStorage.setItem(NAMES_KEY, JSON.stringify(names));
  } catch {
    // idem : non bloquant.
  }
}

// ───────────────────────────── Schémas → vue du terrain ─────────────────────────────

export type Pos = { nx: number; ny: number };
export type StepRaw = {
  label?: string;
  positions?: Record<string, Pos>;
  substitutes?: Record<string, Pos>;
  ball?: Pos;
  quiz?: boolean;
};
export type SchemaLite = {
  id: string;
  name: string;
  type: "service" | "reception";
  playerCount: 4 | 6;
  tactic: string;
  steps: StepRaw[];
};
// `row` : ligne de la joueuse dans l'ordre de rotation (avant / arrière), indépendante de l'endroit où
// elle se trouve à l'étape affichée — en réception, une avant peut reculer pour défendre.
export type Row = "front" | "back";
export type Puck = { id: string; nx: number; ny: number; server: boolean; row: Row | null };
export type CourtView = { pucks: Puck[]; ball: Pos | null };

// Géométrie du terrain dans les schémas (mêmes marges que le studio, le quiz et /admin-rotations).
export const COURT_MX = 0.11;
export const COURT_MY = 0.08;
const COURT_LW = 1 - COURT_MX * 2;
const COURT_LH = 1 - COURT_MY * 2;
// Distance max (en coordonnées normalisées) entre le ballon et la pastille de la serveuse.
const SERVER_MAX_DIST = 0.12;

const isPos = (p: any): p is Pos => !!p && Number.isFinite(p.nx) && Number.isFinite(p.ny);

export function isOnCourt(pos: Pos): boolean {
  const margin = 0.015;
  return (
    pos.nx >= COURT_MX - margin &&
    pos.nx <= COURT_MX + COURT_LW + margin &&
    pos.ny >= COURT_MY - margin &&
    pos.ny <= COURT_MY + COURT_LH + margin
  );
}

// Étape du quiz du schéma : celle marquée 🎯 dans le studio, sinon « Étape 1 » (comme le quiz).
export function getQuizStep(steps: StepRaw[]): StepRaw | undefined {
  return steps.find((s) => s.quiz === true) ?? steps.find((s) => s.label?.trim() === "Étape 1");
}

// Numéro de rotation (1-6) extrait du nom du schéma, ex: "P3 Reception" -> 3.
export function rotationOf(name: string): Rotation | null {
  const m = name.match(/P\s*([1-6])/i);
  return m ? (Number(m[1]) as Rotation) : null;
}

// Pastilles à afficher pour une étape : celles sur le terrain (titulaires + remplaçantes jouées).
// Au service, la serveuse est derrière la ligne de fond donc hors terrain : on la retrouve
// grâce au ballon, posé juste à côté d'elle dans le schéma.
export function buildCourtView(step: StepRaw | undefined, withServer: boolean, rotation?: Rotation): CourtView | null {
  if (!step) return null;
  const all = [...Object.entries(step.positions ?? {}), ...Object.entries(step.substitutes ?? {})]
    .filter(([, pos]) => isPos(pos))
    .map(([id, pos]) => ({ id, pos }));

  const ball = withServer && isPos(step.ball) ? step.ball : null;
  let serverId: string | null = null;
  if (ball) {
    let best = SERVER_MAX_DIST;
    for (let i = 0; i < all.length; i++) {
      const d = Math.hypot(all[i].pos.nx - ball.nx, all[i].pos.ny - ball.ny);
      if (d < best) {
        best = d;
        serverId = all[i].id;
      }
    }
  }

  const pucks = all
    .filter(({ id, pos }) => isOnCourt(pos) || id === serverId)
    .map(({ id, pos }) => ({ id, nx: pos.nx, ny: pos.ny, server: id === serverId, row: rotation ? rowOf(id, rotation) : null }));
  return pucks.length > 0 ? { pucks, ball } : null;
}

// Cran de chaque rôle après la passeuse dans l'ordre de rotation (identifiants canoniques, voir plus bas).
const ROLE_OFFSET: Record<string, number> = { Pass: 0, Sub_R4: 1, C1: 2, Poi: 3, R4: 4, C2: 5 };

// Ligne (avant / arrière) d'une pastille dans la rotation donnée. La libéro remplace toujours une
// centrale arrière ; les pastilles Sub_* de la libéro et des centrales désignent la même joueuse.
export function rowOf(puckId: string, rotation: Rotation): Row | null {
  if (puckId === "Lib" || puckId === "Sub_Lib") return "back";
  const base = puckId === "Sub_R4" ? puckId : puckId.startsWith("Sub_") ? puckId.slice(4) : puckId;
  const offset = ROLE_OFFSET[base];
  if (offset === undefined) return null;
  return isFrontZone(zoneAfter(rotation, offset)) ? "front" : "back";
}

// Étape « Réglementaire » d'un schéma (positions légales avant le service), sinon la première.
export const regulatoryStep = (steps: StepRaw[]): StepRaw | undefined =>
  steps.find((s) => s.label?.trim().toLowerCase() === "réglementaire") ?? steps[0];

// ───────────────────────────── Identité des deux R4 et des deux centrales ─────────────────────────────
// En 5-1, l'ordre des joueuses autour du terrain est fixe : après la passeuse, dans le sens de la
// rotation, viennent la R4 avant (zone 2 en P1), la centrale 1, la pointue, la R4 arrière (zone 5
// en P1) et la centrale 2. Les schémas sont dessinés rotation par rotation et les pastilles de même
// rôle (R4 / Sub_R4, C1 / C2) y sont parfois interverties d'une rotation à l'autre : à l'écran, une
// joueuse semblait alors sauter d'un côté du terrain à l'autre au changement de rotation. On
// réattribue donc les identifiants d'après l'étape « Réglementaire », où chacune est dans sa zone.

// Zone (1-6) d'une joueuse placée `offset` crans après la passeuse quand celle-ci est en zone `rotation`.
const zoneAfter = (rotation: Rotation, offset: number): number => ((rotation - 1 + offset) % 6) + 1;

// Centre d'une zone dans les coordonnées des schémas (filet en haut : zones 4-3-2 devant, 5-6-1 derrière).
function zoneCenter(zone: number): Pos {
  const col = zone === 1 || zone === 2 ? 5 / 6 : zone === 3 || zone === 6 ? 1 / 2 : 1 / 6;
  const front = zone >= 2 && zone <= 4;
  return { nx: COURT_MX + COURT_LW * col, ny: COURT_MY + COURT_LH * (front ? 1 / 4 : 3 / 4) };
}

// Paires de pastilles de même rôle, avec leur cran après la passeuse. Sub_C1 / Sub_C2 désignent la
// même joueuse que C1 / C2 : elles suivent l'échange.
const ROLE_PAIRS: { a: string; offA: number; b: string; offB: number; linked: [string, string][] }[] = [
  { a: "Sub_R4", offA: 1, b: "R4", offB: 4, linked: [] },
  { a: "C1", offA: 2, b: "C2", offB: 5, linked: [["Sub_C1", "Sub_C2"]] },
];

const dist = (a: Pos, b: Pos): number => Math.hypot(a.nx - b.nx, a.ny - b.ny);

// Ids à échanger pour que chaque paire respecte l'ordre de rotation à l'étape de référence :
// on garde l'affectation qui rapproche le plus les deux pastilles de leurs zones attendues.
function roleSwaps(ref: StepRaw, rotation: Rotation): Map<string, string> {
  const all: Record<string, Pos> = { ...(ref.positions ?? {}), ...(ref.substitutes ?? {}) };
  const swaps = new Map<string, string>();
  ROLE_PAIRS.forEach(({ a, offA, b, offB, linked }) => {
    const pa = all[a];
    const pb = all[b];
    if (!isPos(pa) || !isPos(pb)) return;
    const za = zoneCenter(zoneAfter(rotation, offA));
    const zb = zoneCenter(zoneAfter(rotation, offB));
    if (dist(pa, za) + dist(pb, zb) <= dist(pa, zb) + dist(pb, za)) return;
    [[a, b], ...linked].forEach(([x, y]) => {
      swaps.set(x, y);
      swaps.set(y, x);
    });
  });
  return swaps;
}

function renameIds(rec: Record<string, Pos> | undefined, swaps: Map<string, string>): Record<string, Pos> | undefined {
  if (!rec) return rec;
  const out: Record<string, Pos> = {};
  Object.entries(rec).forEach(([id, pos]) => (out[swaps.get(id) ?? id] = pos));
  return out;
}

// La libéro ne joue jamais en ligne avant : quand la centrale qu'elle remplaçait revient devant
// (ex. P5 au service, où la centrale arrière arrive en zone 4), c'est la centrale qui doit être sur
// le terrain et la libéro sur le banc. Certains schémas laissent la libéro devant : on échange alors
// sa place avec celle de la centrale attendue en ligne avant dans cette rotation, si elle est restée
// hors du terrain (l'autre centrale, derrière, peut être en train de servir : on n'y touche pas).
const isFrontRow = (pos: Pos): boolean => isOnCourt(pos) && pos.ny < COURT_MY + COURT_LH / 3;
const isFrontZone = (zone: number): boolean => zone >= 2 && zone <= 4;

function liberoOutOfFrontRow(step: StepRaw, rotation: Rotation): StepRaw {
  const positions = { ...(step.positions ?? {}) };
  const substitutes = { ...(step.substitutes ?? {}) };
  const at = (id: string): Pos | undefined => positions[id] ?? substitutes[id];
  const libId = ["Lib", "Sub_Lib"].find((id) => isPos(at(id)) && isFrontRow(at(id)!));
  const cId = isFrontZone(zoneAfter(rotation, 2)) ? "C1" : "C2";
  if (!libId || !isPos(at(cId)) || isOnCourt(at(cId)!)) return step;
  const place = (id: string, pos: Pos) => (id in positions ? (positions[id] = pos) : (substitutes[id] = pos));
  const libPos = at(libId)!;
  place(libId, at(cId)!);
  place(cId, libPos);
  return { ...step, positions, substitutes };
}

// Étapes d'un schéma Pn avec des identifiants cohérents d'une rotation à l'autre (voir ci-dessus)
// et la libéro hors de la ligne avant.
export function canonicalSteps(steps: StepRaw[], rotation: Rotation): StepRaw[] {
  const ref = regulatoryStep(steps);
  if (!ref) return steps;
  const swaps = roleSwaps(ref, rotation);
  const renamed =
    swaps.size === 0 ? steps : steps.map((s) => ({ ...s, positions: renameIds(s.positions, swaps), substitutes: renameIds(s.substitutes, swaps) }));
  return renamed.map((s) => liberoOutOfFrontRow(s, rotation));
}

// Par rotation et par phase : la vue affichée, et la position « Réglementaire » du même schéma
// (pour vérifier d'un appui long que la rotation est la bonne).
export type RotationViews = Record<
  Rotation,
  { reception: CourtView | null; service: CourtView | null; regulatory: { reception: CourtView | null; service: CourtView | null } }
>;

// Un schéma 6 joueuses par rotation et par type (préférence aux tactiques « 5-1 »), comme /admin-rotations.
export function pickRotationViews(schemas: SchemaLite[]): RotationViews {
  const views = {} as RotationViews;
  ROTATIONS.forEach((r) => (views[r] = { reception: null, service: null, regulatory: { reception: null, service: null } }));

  (["reception", "service"] as const).forEach((type) => {
    const chosen = new Map<Rotation, SchemaLite>();
    const candidates = schemas.filter((s) => s.type === type && s.playerCount === 6);
    const pick = (list: SchemaLite[]) =>
      list.forEach((s) => {
        const rot = rotationOf(s.name);
        if (rot && !chosen.has(rot)) chosen.set(rot, s);
      });
    pick(candidates.filter((s) => s.tactic.replace(/\s/g, "").includes("5-1")));
    pick(candidates);

    chosen.forEach((schema, rot) => {
      const steps = canonicalSteps(schema.steps, rot);
      // Réception : étape du quiz. Service : étape 0 (la 1re, « Réglementaire »).
      views[rot][type] = type === "reception" ? buildCourtView(getQuizStep(steps), false, rot) : buildCourtView(steps[0], true, rot);
      views[rot].regulatory[type] = buildCourtView(regulatoryStep(steps), type === "service", rot);
    });
  });
  return views;
}
